import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { MessageSquare, Send, Loader2, ImageIcon, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { canWriteOrderNotes } from "@/lib/role-access";
import { useMentionableUsers, mentionLabel, type MentionableUser } from "@/hooks/useMentionableUsers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { validateImageFiles } from "@/lib/validation";
import { OrderNoteImagePreviewDialog, type NoteImageRef } from "./OrderNoteImagePreviewDialog";
import { faro } from "@grafana/faro-web-sdk";

const MAX_NOTE_IMAGES = 4;

/**
 * Structured failure reporting for the note-write path.
 *
 * A user-reported "Cannot coerce the result to a single JSON object" was not
 * diagnosable because only `code` + `message` ever reached the UI. Every
 * failure in this flow now carries the full PostgREST envelope (code, details,
 * hint) plus the ids involved, to the console AND to Faro, so a repeat report
 * is greppable without a screenshot.
 */
function reportNoteFailure(
  stage: string,
  ctx: Record<string, unknown>,
  err: { code?: string; message?: string; details?: string; hint?: string } | null,
): string {
  const payload = {
    stage,
    ...ctx,
    code: err?.code ?? null,
    message: err?.message ?? null,
    details: err?.details ?? null,
    hint: err?.hint ?? null,
  };
  console.error(`[order-notes] ${stage} failed`, payload);
  try {
    // Faro is initialised at app boot; be defensive so a missing collector
    // never breaks the note flow itself.
    faro?.api?.pushError?.(new Error(`[order-notes] ${stage}: ${err?.message ?? "unknown"}`), {
      context: Object.fromEntries(
        Object.entries(payload).map(([k, v]) => [k, v == null ? "" : String(v)]),
      ),
    });
  } catch {
    /* reporting must never mask the underlying error */
  }
  const parts = [err?.code ?? "unknown", err?.message ?? "no message"];
  if (err?.details) parts.push(err.details);
  if (err?.hint) parts.push(`Hint: ${err.hint}`);
  return parts.join(" — ");
}

interface OrderNoteImage {
  id: string;
  note_id: string;
  file_path: string;
  file_name: string | null;
  mime_type: string | null;
}

interface OrderNote {
  id: string;
  order_id: string;
  content: string;
  created_by_user_id: string;
  created_by_email: string | null;
  created_at: string;
  deleted_at: string | null;
  mentioned_user_ids?: string[] | null;
}

function sanitize(input: string): string {
  return input.replace(/<[^>]*>/g, "").trim().slice(0, 2000);
}

/** Render note content with @mentions visually highlighted. */
function renderNoteContent(content: string, users: MentionableUser[]): React.ReactNode {
  if (!users.length) return content;
  // Build a regex that matches any "@<label>" for known users (longest first
  // so "@Jane Doe" wins over "@Jane").
  const labels = users
    .map(mentionLabel)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!labels.length) return content;
  const re = new RegExp(`@(?:${labels.join("|")})`, "g");
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    parts.push(
      <span
        key={`m-${key++}`}
        className="inline-flex items-center rounded bg-primary/15 text-primary px-1 py-0.5 text-xs font-medium"
      >
        {m[0]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts;
}

interface OrderNotesCardProps {
  orderId: string;
  /** Compact variant for the side sheet */
  compact?: boolean;
  /** Hide the inner card header (when the parent already shows a section title) */
  hideHeader?: boolean;
}

export function OrderNotesCard({ orderId, compact, hideHeader }: OrderNotesCardProps) {
  const { user } = useAuth();
  const { roles } = useUserRole();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [mentionedIds, setMentionedIds] = useState<Set<string>>(new Set());
  const [mentionQuery, setMentionQuery] = useState<string | null>(null); // null = picker closed
  const [mentionAnchorIndex, setMentionAnchorIndex] = useState<number>(-1);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { data: mentionables = [] } = useMentionableUsers();
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [previewState, setPreviewState] = useState<{ images: NoteImageRef[]; index: number } | null>(null);

  // Stable object URLs per pending file so re-renders don't flicker.
  const pendingPreviewUrls = useMemo(
    () => pendingImages.map((f) => URL.createObjectURL(f)),
    [pendingImages]
  );
  useEffect(() => {
    return () => {
      pendingPreviewUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [pendingPreviewUrls]);

  const canWrite = canWriteOrderNotes(roles);

  const { data: orderUniqueId } = useQuery({
    queryKey: ["order-unique-id", orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("unique_id")
        .eq("id", orderId)
        .maybeSingle();
      if (error) throw error;
      return (data?.unique_id as string | null) ?? null;
    },
    enabled: !!orderId,
    staleTime: 60_000,
  });

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["order-notes", orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_notes")
        .select("*")
        .eq("order_id", orderId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as OrderNote[];
    },
    enabled: !!orderId,
  });

  // All attached images for this order, grouped by note.
  const { data: noteImages = [] } = useQuery({
    queryKey: ["order-note-images", orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_note_images")
        .select("id, note_id, file_path, file_name, mime_type")
        .eq("order_id", orderId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as OrderNoteImage[];
    },
    enabled: !!orderId,
  });

  const imagesByNote = useMemo(() => {
    const m = new Map<string, OrderNoteImage[]>();
    for (const img of noteImages) {
      const arr = m.get(img.note_id) ?? [];
      arr.push(img);
      m.set(img.note_id, arr);
    }
    return m;
  }, [noteImages]);

  // Batch-signed thumbnail URLs (10 min) for all attachments on this order.
  const allPaths = useMemo(() => noteImages.map((i) => i.file_path), [noteImages]);
  const pathsKey = allPaths.join("|");
  const { data: thumbUrls = {} } = useQuery({
    queryKey: ["order-note-image-urls", orderId, pathsKey],
    queryFn: async () => {
      if (allPaths.length === 0) return {} as Record<string, string>;
      const { data, error } = await supabase.storage
        .from("order-note-images")
        .createSignedUrls(allPaths, 600);
      if (error) throw error;
      const out: Record<string, string> = {};
      for (const row of data ?? []) {
        if (row.path && row.signedUrl) out[row.path] = row.signedUrl;
      }
      return out;
    },
    enabled: allPaths.length > 0,
    staleTime: 9 * 60_000,
    refetchInterval: 9 * 60_000,
  });

  const addMutation = useMutation({
    mutationFn: async (payload: { content: string; mentioned_user_ids: string[]; files: File[] }) => {
      // Look up the parent order's tenant so the note row matches the order
      // (avoids the column default leaking the prod tenant onto cross-tenant notes,
      // which then trips the SELECT RLS and surfaces as a delete failure).
      const { data: orderRow, error: orderErr } = await supabase
        .from("orders")
        .select("tenant_id")
        .eq("id", orderId)
        .maybeSingle();
      if (orderErr) {
        // Surface the real PostgREST failure (code + details + hint) instead of a
        // bare message, so a report like "it fails on every order" is diagnosable.
        const detail = reportNoteFailure(
          "tenant-lookup",
          { orderId, userId: user?.id ?? null },
          orderErr,
        );
        throw new Error(`Couldn't load this order — ${detail}`);
      }
      if (!orderRow?.tenant_id) {
        reportNoteFailure(
          "tenant-lookup-empty",
          { orderId, userId: user?.id ?? null },
          null,
        );
        throw new Error(
          `Could not resolve this order's workspace (order ${orderId}) — you may not have access to it. Refresh and try again.`,
        );
      }
      const tenantId = orderRow.tenant_id as string;
      const { data: noteRow, error } = await supabase
        .from("order_notes")
        .insert({
          order_id: orderId,
          content: payload.content,
          created_by_user_id: user!.id,
          created_by_email: user!.email ?? null,
          mentioned_user_ids: payload.mentioned_user_ids,
          tenant_id: tenantId,
        })
        .select("id")
        .maybeSingle();
      if (error) {
        const detail = reportNoteFailure(
          "insert",
          { orderId, tenantId, userId: user?.id ?? null },
          error,
        );
        throw new Error(`Note failed to save — ${detail}`);
      }
      // The row can insert successfully yet be invisible to the read-back when
      // the caller's read scope is narrower than their write scope. Only fail
      // loudly if we still need the note id to attach images.
      if (!noteRow?.id) {
        reportNoteFailure(
          "insert-readback-empty",
          { orderId, tenantId, userId: user?.id ?? null, files: payload.files.length },
          null,
        );
        if (payload.files.length > 0) {
          throw new Error("Note saved, but images could not be attached. Please re-upload them.");
        }
        return;
      }

      // Upload images and attach them to the freshly-created note.
      for (const file of payload.files) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `${tenantId}/${noteRow.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("order-note-images")
          .upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type });
        if (upErr) {
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }
        const { error: insErr } = await supabase.from("order_note_images").insert({
          note_id: noteRow.id,
          order_id: orderId,
          tenant_id: tenantId,
          file_path: path,
          file_name: file.name,
          mime_type: file.type || null,
          size_bytes: file.size,
          uploaded_by_user_id: user!.id,
          uploaded_by_email: user!.email ?? null,
        });
        if (insErr) {
          toast.error(`Failed to attach ${file.name}`);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-notes", orderId] });
      queryClient.invalidateQueries({ queryKey: ["order-note-images", orderId] });
      setDraft("");
      setMentionedIds(new Set());
      setMentionQuery(null);
      setPendingImages([]);
      if (imageInputRef.current) imageInputRef.current.value = "";
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSend = () => {
    const cleaned = sanitize(draft);
    if (!cleaned && pendingImages.length === 0) return;
    // Prune mentioned ids whose label no longer appears in the text.
    const finalIds: string[] = [];
    for (const u of mentionables) {
      if (!mentionedIds.has(u.user_id)) continue;
      const label = mentionLabel(u);
      if (label && cleaned.includes(`@${label}`)) finalIds.push(u.user_id);
    }
    addMutation.mutate({ content: cleaned, mentioned_user_ids: finalIds, files: pendingImages });
  };

  const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const { valid, errors } = validateImageFiles(files);
    errors.forEach((err) => toast.error(err));
    if (valid.length) {
      setPendingImages((prev) => {
        const room = Math.max(0, MAX_NOTE_IMAGES - prev.length);
        if (room === 0) {
          toast.error(`You can attach up to ${MAX_NOTE_IMAGES} images per note`);
          return prev;
        }
        const added = valid.slice(0, room);
        if (valid.length > room) {
          toast.error(`Only ${room} more image${room === 1 ? "" : "s"} allowed`);
        }
        return [...prev, ...added];
      });
    }
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const removePendingImage = (idx: number) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  // Watch the textarea for an open `@query` token immediately before the caret.
  const handleDraftChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDraft(value);
    const caret = e.target.selectionStart ?? value.length;
    const upToCaret = value.slice(0, caret);
    const at = upToCaret.lastIndexOf("@");
    if (at === -1) {
      setMentionQuery(null);
      return;
    }
    // Only trigger if @ is at start or preceded by whitespace.
    const prev = at === 0 ? " " : upToCaret[at - 1];
    if (!/\s/.test(prev)) {
      setMentionQuery(null);
      return;
    }
    const token = upToCaret.slice(at + 1);
    if (/\s/.test(token)) {
      setMentionQuery(null);
      return;
    }
    setMentionAnchorIndex(at);
    setMentionQuery(token.toLowerCase());
  };

  const filteredMentionables = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery;
    return mentionables
      .filter((u) => u.user_id !== user?.id)
      .filter((u) => {
        if (!q) return true;
        const label = mentionLabel(u).toLowerCase();
        return label.includes(q) || u.email.toLowerCase().includes(q);
      })
      .slice(0, 6);
  }, [mentionables, mentionQuery, user?.id]);

  const insertMention = (u: MentionableUser) => {
    if (mentionAnchorIndex < 0) return;
    const label = mentionLabel(u);
    const before = draft.slice(0, mentionAnchorIndex);
    // Remove the in-progress `@token` after the anchor up to the caret/space.
    const afterStart = mentionAnchorIndex + 1;
    const restAfterAt = draft.slice(afterStart);
    const wsMatch = restAfterAt.match(/\s/);
    const tokenEnd = wsMatch ? afterStart + (wsMatch.index ?? 0) : draft.length;
    const after = draft.slice(tokenEnd);
    const inserted = `@${label} `;
    const next = before + inserted + after;
    setDraft(next);
    setMentionedIds((prev) => {
      const n = new Set(prev);
      n.add(u.user_id);
      return n;
    });
    setMentionQuery(null);
    // Restore caret after the inserted mention.
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      const pos = before.length + inserted.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  return (
    <Card>
      {!hideHeader && (
        <CardHeader className={compact ? "pb-2" : "pb-3"}>
          <CardTitle className={`${compact ? "text-sm font-semibold" : "text-base"} flex items-center gap-2`}>
            <MessageSquare className="h-4 w-4" />
            Notes
            {notes.length > 0 && (
              <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                {notes.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={`space-y-3 ${hideHeader ? (compact ? "pt-4" : "pt-6") : ""}`}>
        {/* Input area — hidden for roles that can't write */}
        {canWrite && (
          <div className="space-y-2">
            <div className="flex gap-2 items-end relative">
              <div className="flex-1 relative">
                <Textarea
                  ref={textareaRef}
                  placeholder="Add a note… use @ to mention"
                  value={draft}
                  onChange={handleDraftChange}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && mentionQuery !== null) {
                      e.preventDefault();
                      setMentionQuery(null);
                    }
                  }}
                  className={`text-sm resize-none ${compact ? 'min-h-[36px] h-9 py-2' : 'min-h-[60px]'}`}
                  maxLength={2000}
                />
                {mentionQuery !== null && filteredMentionables.length > 0 && (
                  <div className="absolute z-50 top-full mt-1 left-0 w-64 rounded-md border bg-popover shadow-md overflow-hidden">
                    <ul className="max-h-56 overflow-y-auto py-1 text-sm">
                      {filteredMentionables.map((u) => (
                        <li key={u.user_id}>
                          <button
                            type="button"
                            onClick={() => insertMention(u)}
                            className="w-full text-left px-2 py-1.5 hover:bg-accent flex flex-col"
                          >
                            <span className="font-medium">{mentionLabel(u)}</span>
                            <span className="text-xs text-muted-foreground truncate">{u.email}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImagePick}
              />
              <Button
                size="icon"
                variant="outline"
                className="shrink-0 self-end"
                onClick={() => imageInputRef.current?.click()}
                disabled={pendingImages.length >= MAX_NOTE_IMAGES || addMutation.isPending}
                title="Add image"
                aria-label="Add image"
              >
                <ImageIcon className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                className="shrink-0 self-end"
                disabled={(!draft.trim() && pendingImages.length === 0) || addMutation.isPending}
                onClick={handleSend}
                title="Send note"
                aria-label="Send note"
              >
                {addMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            {pendingImages.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {pendingImages.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} className="relative h-16 w-16 rounded-md overflow-hidden border bg-muted">
                      <img
                        src={pendingPreviewUrls[idx]}
                        alt={file.name}
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePendingImage(idx)}
                        className="absolute -top-1 -right-1 bg-background border rounded-full p-0.5 shadow hover:bg-destructive hover:text-destructive-foreground"
                        aria-label={`Remove ${file.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes list (newest first) */}
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : notes.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {notes.map((note) => (
              <div key={note.id} className="border rounded-lg p-3 space-y-1 group">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground truncate max-w-[70%]">
                    {note.created_by_email ?? "Unknown"}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(note.created_at), "MMM dd 'at' hh:mm a")}
                    </span>
                  </div>
                </div>
                {note.content && (
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {renderNoteContent(note.content, mentionables)}
                  </p>
                )}
                {(() => {
                  const imgs = imagesByNote.get(note.id) ?? [];
                  if (imgs.length === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {imgs.map((img, i) => (
                        <button
                          key={img.id}
                          type="button"
                          onClick={() =>
                            setPreviewState({
                              images: imgs.map((x) => ({ id: x.id, file_path: x.file_path, file_name: x.file_name })),
                              index: i,
                            })
                          }
                          className="h-16 w-16 rounded-md overflow-hidden border bg-muted hover:opacity-90 transition"
                          aria-label={`View ${img.file_name ?? "attached image"}`}
                        >
                          {thumbUrls[img.file_path] ? (
                            <img
                              src={thumbUrls[img.file_path]}
                              alt={img.file_name ?? "Note attachment"}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center">
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        ) : null}
        {previewState && (
          <OrderNoteImagePreviewDialog
            open={!!previewState}
            onOpenChange={(o) => !o && setPreviewState(null)}
            images={previewState.images}
            initialIndex={previewState.index}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** Lightweight hook to fetch note count for a given order (used by AssignmentCard). */
export function useOrderNoteCount(orderId: string | undefined) {
  return useQuery({
    queryKey: ["order-notes-count", orderId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("order_notes")
        .select("*", { count: "exact", head: true })
        .eq("order_id", orderId!)
        .is("deleted_at", null);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!orderId,
  });
}
